<template>
  <v-app id="inspire" :style="{ background: $vuetify.theme.themes[theme].background }">
    <v-dialog v-model="loadingUserAndPortfolio" persistent content-class="loading-dialog" width="300">
      <v-card color="primary" dark>
        <v-card-text>
          <span class="d-block font-weight-bold pt-2">
            {{ $t('globals.loading') }}
          </span>
          <v-progress-linear class="mt-3" indeterminate color="white" />
        </v-card-text>
      </v-card>
    </v-dialog>
    <NavigationDrawer
      :is-open="isDrawerOpen"
      @set-open="setDrawerOpenState"
      :items="items"
      current-id="paul"
    />
    <div class="appContentContainer">
      <v-app-bar color="white" elevation="0" class="flex-grow-0 appBar" :height="48">
        <div class="appBarContent">
          <Navigation @showDrawer="isDrawerOpen = true" />
          <v-toolbar-title white--text class="d-inline-flex flex-shrink-0">
            NOOS
          </v-toolbar-title>
          <div class="align-center right-items">
            <v-btn
              v-if="userIsOrgAdmin"
              class="mx-4"
              small
              outlined
              color="primary"
              @click.stop.prevent.exact="toAdmin"
            >
              ADMIN
            </v-btn>
            <v-toolbar-items class="align-center">
              <v-dialog
                :close-on-content-click="false"
                v-model="showSearchDialog"
                :fullscreen="$vuetify.breakpoint.xs"
                max-width="1864px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    @click.stop.prevent
                    @click.exact="onClick"
                    small
                    class="searchIcon"
                  >
                    fa-search
                  </v-icon>
                </template>
                <SectionCreator @close-section-creator="showSearchDialog = false;" />
              </v-dialog>
              <v-menu
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn text icon v-bind="attrs" v-on="on">
                    <v-badge eager overlap color="orange" v-model="showNotificationsBadge">
                      <template v-slot:badge>
                        <span>{{ notViewedNotificationCount }}</span>
                      </template>
                      <v-icon alt="Notifications">
                        notifications
                      </v-icon>
                    </v-badge>
                  </v-btn>
                </template>
                <NotificationsMenu />
              </v-menu>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-avatar style="align-self: center;" size="36px">
                      <v-icon>
                        account_circle
                      </v-icon>
                    </v-avatar>
                    <span style="align-self: center;">
                      {{ displayName }}
                    </span>
                  </div>
                </template>
                <v-list class="pa-1">
                  <v-list-item v-if="isAdmin" class="px-2 list-item">
                    <v-list-item-title>
                      <a
                        href="/admin"
                        class="menu-item"
                        @click.stop
                      >
                        Admin
                      </a>
                      <v-divider class="mt-1" />
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isAdmin" class="px-2 list-item">
                    <v-list-item-title>
                      <a
                        href="/analytics"
                        class="menu-item"
                        @click.stop
                      >
                        Analytics
                      </a>
                      <v-divider class="mt-1" />
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-2 list-item">
                    <v-list-item-title>
                      <a
                        href="/logout"
                        class="menu-item"
                        @click.stop
                      >
                        {{ $t('actions.disconnect') }}
                      </a>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-toolbar-items>
          </div>
        </div>
      </v-app-bar>
      <v-main app inset class="flex-grow-1 pageContainer">
        <v-container v-if="isReady" class="viewContainer fill-height" fluid>
          <router-view :key="$route.fullPath" v-if="!loadingUserAndPortfolio" />
        </v-container>
      </v-main>
    </div>
    <v-snackbar top v-model="showSnackbar" :color="snackbarColor">
      {{ $t(snackbarMessage) }}
      <v-btn dark text @click="hideSnackbar" id="close-snackbar-button">
        {{ $t('actions.close') }}
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import NotificationsMenu from '@/shared/components/notification/NotificationsMenu';
import { types } from '../store/rootTypes';
import { currentVersion } from '../tools/currentVersion';
import SectionCreator from '../components/pages/journal/SectionCreator';
import Navigation from '../components/navigation/Navigation';
import NavigationDrawer from '../components/navigation/NavigationDrawer';

const dispatchLoginActions = async (store) => {
  await Promise.all([
    store.dispatch(types.authentication.actions.fetchUserPreferences),
    store.dispatch(types.user.actions.fetchUserDetails),
  ]);
  await store.dispatch(types.globalConfig.actions.fetchGlobalConfig);

  store.dispatch(types.templates.actions.fetchTemplates);
  store.dispatch(types.portfolio.actions.fetchPortfolio);
  store.dispatch(types.authentication.actions.fetchOrganizationTags);
  store.dispatch(types.notifications.actions.fetchNotifications);

  // Should be done before journal
  await store.dispatch(types.reflections.actions.updateFetchedReflections);
  store.dispatch(types.recommendationBasedJournals.actions.fetchPipelinedJournal);
  store.dispatch(types.recommendationBasedJournals.actions.fetchOrgJournal);
  store.dispatch(types.recommendationBasedJournals.actions.fetchSpotlightJournal);
};

export default {
  name: 'AuthenticatedApp',
  components: {
    SectionCreator,
    Navigation,
    NavigationDrawer,
    NotificationsMenu,
  },
  data() {
    return {
      isDrawerOpen: false,
      showSearchDialog: false,
      isReady: false,
    };
  },
  props: {
    title: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      idTokenReady: types.authentication.getters.idTokenReady,
      isAdmin: types.authentication.getters.isAdmin,
      userReady: types.user.getters.userReady,
      userIsOrgAdmin: types.user.getters.userIsOrgAdmin,
      displayName: types.authentication.getters.displayName,
      notViewedNotificationCount: types.notifications.getters.notViewedNotificationCount,
      userJournalRead: types.authentication.getters.userJournalRead,
    }),
    ...mapState(['snackbarColor', 'snackbarMessage']),
    theme() {
      return (this.$vuetify.theme.dark) ? 'dark' : 'light';
    },
    items() {
      return [
        {
          id: 'home',
          title: this.$t('navigation.home.menu'),
          icon: 'dashboard',
          path: '/home',
        },
        {
          id: 'portfolio',
          title: this.$t('mainSections.portfolio.long'),
          icon: 'fa-folder',
          path: '/portfolio',
        },
        {
          id: 'newspaper',
          title: this.$t('mainSections.journal.long'),
          icon: 'fa-newspaper',
          path: '/journalv2',
          indicator: !this.userJournalRead,
        },
        {
          id: 'logout',
          title: this.$t('actions.disconnect'),
          icon: 'power_settings_new',
          path: '/logout',
        },
      ];
    },
    showSnackbar: {
      get() {
        return this.$store.state.showSnackbar;
      },
      set(value) {
        if (value) {
          this.$store.commit(types.mutations.SHOW_SNACKBAR, {
            message: this.snackbarMessage,
            color: this.snackbarColor,
          });
        } else {
          this.$store.commit(types.mutations.HIDE_SNACKBAR);
        }
      },
    },
    loadingUserAndPortfolio() {
      return !(this.idTokenReady);
    },
    showNotificationsBadge() {
      return this.notViewedNotificationCount > 0;
    },
    version() {
      return currentVersion();
    },
    routeName() {
      return this.$route.name;
    },
  },
  created() {
    if (this.idTokenReady) {
      dispatchLoginActions(this.$store);
    }
    this.isReady = this.userReady;
  },
  methods: {
    ...mapMutations({
      hideSnackbar: types.mutations.HIDE_SNACKBAR,
    }),
    setDrawerOpenState(newValue) {
      this.isDrawerOpen = newValue;
    },
    onClick() {
      this.showSearchDialog = !this.showSearchDialog;
    },
    toAdmin() {
      global.location.href = '/org-admin';
    },
  },
  watch: {
    idTokenReady(status) {
      if (status) {
        dispatchLoginActions(this.$store);
      }
    },
    userReady() {
      this.isReady = this.userReady;
    },
  },
};


</script>
<style scoped>
.appBar {
  border-bottom: solid 1px #ddd !important;
  z-index: 1000;
}
.viewContainer {
  overflow-x: auto;
  width: 100vw;
  padding: 0px;
}

.appContentContainer .appBarContent {
  display: flex;
  height: 48px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.appContentContainer {
  display: flex;
  width: 100%;
  flex-direction: column;
  height: 100vh;
}

.pageContainer {
  max-height: calc(100vh - 48px);
  overflow: auto;
}

.v-btn--active span {
  border-bottom: solid 1px #aaa;
  color: blue;
}
.searchIcon {
  width: 30px;
  height: 30px;
}
.menu-item {
  text-decoration: none;
  color: black;
}
.list-item {
  display: flex;
  flex-direction: row;
  min-height: 24px;
  margin-bottom: 2px;
  margin-top: 2px;
}
.right-items {
  width: 550px;
  display: flex;
  justify-content: flex-end;
}
</style>
